import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  InputGroup
} from 'react-bootstrap';

import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';
import ImageReorderGrid from '../components/ImageReorderGrid';
import ImageCropperModal from '../components/ImageCropperModal';

function EditProductPage() {
  const navigate = useNavigate();
  const location = useLocation();

  /* Where to return after saving — the page that launched the edit. */
  const returnTo = location.state?.from || '/admin/products';

  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  /* Category list comes from the DB, so an admin-created category is
     selectable here — the old hardcoded list couldn't hold custom ones. */
  const [categories, setCategories] = useState([]);

  const [imageFiles, setImageFiles] = useState([]);

  const MIN_IMAGES = 5;

  const [product, setProduct] = useState({
    name: '',

    category: '',

    image: '',

    price: '',

    stock: '',

    topSale: false,

    shortDescription: '',

    description: '',

    highlights: ''
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(
          `${API}/products/${id}`
        );

        setProduct(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(
          `${API}/categories`
        );

        setCategories(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setProduct({
      ...product,

      [e.target.name]: e.target.value
    });
  };

  /* Reorder / remove newly-selected files */
  const moveNewImage = (from, to) => {
    if (to < 0 || to >= imageFiles.length) return;
    const next = [...imageFiles];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setImageFiles(next);
  };
  const removeNewImage = (index) =>
    setImageFiles(imageFiles.filter((_, i) => i !== index));

  /* Crop / adjust a newly-selected image before upload */
  const [cropIndex, setCropIndex] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);

  const openCropper = (index) => {
    setCropIndex(index);
    setCropSrc(URL.createObjectURL(imageFiles[index]));
  };

  const applyCrop = (file) => {
    setImageFiles(imageFiles.map((f, i) => (i === cropIndex ? file : f)));
    setCropIndex(null);
    setCropSrc(null);
  };

  /* Reorder / remove the product's existing saved images */
  const currentImages = () =>
    product.images && product.images.length
      ? [...product.images]
      : product.image
        ? [product.image]
        : [];

  const moveExistingImage = (from, to) => {
    const imgs = currentImages();
    if (to < 0 || to >= imgs.length) return;
    const [item] = imgs.splice(from, 1);
    imgs.splice(to, 0, item);
    setProduct({ ...product, images: imgs, image: imgs[0] });
  };
  const removeExistingImage = (index) => {
    const imgs = currentImages().filter((_, i) => i !== index);
    setProduct({ ...product, images: imgs, image: imgs[0] || '' });
  };

  const uploadImages = async () => {
    const formData = new FormData();

    imageFiles.forEach((file) => formData.append('images', file));

    try {
      const { data } = await axios.post(
        `${API}/upload/multiple`,

        formData,

        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return data.images || [];
    } catch (error) {
      console.log(error);

      return [];
    }
  };

  const updateProduct = async (e) => {
    e.preventDefault();

    /* Only validate when the admin is replacing the gallery */
    if (imageFiles.length > 0 && imageFiles.length < MIN_IMAGES) {
      toast.warning(`Please add at least ${MIN_IMAGES} images (or none to keep current)`);
      return;
    }

    try {
      setLoading(true);

      let images =
        product.images && product.images.length
          ? product.images
          : product.image
            ? [product.image]
            : [];

      if (imageFiles.length > 0) {
        const uploaded = await uploadImages();
        if (uploaded.length) {
          images = uploaded;
        }
      }

      await axios.put(
        `${API}/products/${id}`,

        {
          name: product.name,

          category: product.category,

          image: images[0] || product.image,

          images,

          price: Number(product.price),

          stock: Number(product.stock),

          topSale: product.topSale,

          shortDescription: product.shortDescription,

          description: product.description,

          highlights: Array.isArray(product.highlights)
            ? product.highlights
            : (product.highlights || '')
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item !== '')
        }
      );

      toast.success('Product Updated Successfully');

      navigate(returnTo);
    } catch (error) {
      console.log(error);

      toast.error('Update Failed');
    } finally {
      setLoading(false);
    }
  };

  const galleryImages =
    imageFiles.length > 0
      ? imageFiles.map((f) => URL.createObjectURL(f))
      : currentImages();

  const highlightList = Array.isArray(product.highlights)
    ? product.highlights
    : (product.highlights || '')
        .split(',')
        .map((h) => h.trim())
        .filter(Boolean);

  return (
    <>
      <CustomNavbar />

      <div className="ep-wrap">
        <Container>
          {/* Hero */}
          <Card className="border-0 shadow-lg mb-4 ep-hero">
            <span className="ep-hero-blob ep-hero-blob-1" />
            <span className="ep-hero-blob ep-hero-blob-2" />

            <Card.Body className="position-relative p-4 p-md-5">
              <button
                type="button"
                className="ep-back"
                onClick={() => navigate(returnTo)}
              >
                ← Back
              </button>

              <div className="d-flex align-items-center gap-3">
                {galleryImages[0] ? (
                  <img
                    src={galleryImages[0]}
                    alt={product.name}
                    className="ep-hero-thumb"
                  />
                ) : (
                  <span className="ep-hero-icon">✏</span>
                )}

                <div className="min-w-0">
                  <span className="ep-eyebrow">Editing product</span>
                  <h1 className="fw-bold mb-0 ep-hero-title">
                    {product.name || 'Edit Product'}
                  </h1>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Form onSubmit={updateProduct}>
            <Row className="g-4">
              {/* ===== Left: details ===== */}
              <Col lg={7}>
                <Card className="border-0 shadow-sm ep-card mb-4">
                  <Card.Body className="p-4">
                    <div className="ep-legend mb-3">
                      <span className="ep-legend-icon">📝</span>
                      <div>
                        <div className="ep-legend-title">Basic Details</div>
                        <small className="ep-legend-hint">
                          Name, category and pricing
                        </small>
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label className="ep-label">
                        Product Name <span className="ep-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="ep-input"
                        type="text"
                        name="name"
                        placeholder="Product name"
                        value={product.name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="ep-label">
                        Category <span className="ep-req">*</span>
                      </Form.Label>
                      <Form.Select
                        className="ep-input"
                        name="category"
                        value={product.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>

                        {categories.map((category) => (
                          <option key={category._id} value={category.name}>
                            {category.name}
                          </option>
                        ))}

                        {/* Keep the current value selectable even if it isn't
                            in the category list (e.g. a since-deleted one). */}
                        {product.category &&
                          !categories.some(
                            (c) => c.name === product.category
                          ) && (
                            <option value={product.category}>
                              {product.category} (current)
                            </option>
                          )}
                      </Form.Select>
                    </Form.Group>

                    <Row className="g-3">
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label className="ep-label">
                            Price <span className="ep-req">*</span>
                          </Form.Label>
                          <InputGroup className="ep-money">
                            <InputGroup.Text>₹</InputGroup.Text>
                            <Form.Control
                              type="number"
                              min="0"
                              name="price"
                              placeholder="0"
                              value={product.price}
                              onChange={handleChange}
                              required
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>

                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label className="ep-label">
                            Stock <span className="ep-req">*</span>
                          </Form.Label>
                          <Form.Control
                            className={`ep-input${
                              Number(product.stock) === 0 ? ' ep-input-warn' : ''
                            }`}
                            type="number"
                            min="0"
                            name="stock"
                            placeholder="0"
                            value={product.stock}
                            onChange={handleChange}
                            required
                          />
                          {Number(product.stock) === 0 && (
                            <small className="ep-help ep-help-warn">
                              ⚠ Out of stock — customers can't buy this
                            </small>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Top-sale as a real toggle card */}
                    <div
                      className={`ep-toggle mt-3${
                        product.topSale ? ' is-on' : ''
                      }`}
                      onClick={() =>
                        setProduct({ ...product, topSale: !product.topSale })
                      }
                    >
                      <div>
                        <div className="ep-toggle-title">🔥 Top Sale</div>
                        <small className="ep-toggle-hint">
                          Feature this on the storefront best-sellers
                        </small>
                      </div>
                      <Form.Check
                        type="switch"
                        checked={Boolean(product.topSale)}
                        onChange={(e) =>
                          setProduct({ ...product, topSale: e.target.checked })
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm ep-card">
                  <Card.Body className="p-4">
                    <div className="ep-legend mb-3">
                      <span className="ep-legend-icon">📄</span>
                      <div>
                        <div className="ep-legend-title">Description</div>
                        <small className="ep-legend-hint">
                          What the customer reads
                        </small>
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label className="ep-label">
                        Short Description <span className="ep-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="ep-input"
                        type="text"
                        name="shortDescription"
                        placeholder="One-line summary shown on cards"
                        maxLength={160}
                        value={product.shortDescription}
                        onChange={handleChange}
                        required
                      />
                      <small className="ep-help">
                        {(product.shortDescription || '').length}/160 characters
                      </small>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="ep-label">
                        Full Description <span className="ep-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="ep-input"
                        as="textarea"
                        rows={5}
                        name="description"
                        placeholder="Full details shown on the product page"
                        value={product.description}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="ep-label">
                        Product Highlights
                      </Form.Label>
                      <Form.Control
                        className="ep-input"
                        type="text"
                        name="highlights"
                        value={
                          Array.isArray(product.highlights)
                            ? product.highlights.join(', ')
                            : product.highlights || ''
                        }
                        onChange={handleChange}
                        placeholder="e.g. Pure brass, Hand engraved, 6 inches"
                      />
                      <small className="ep-help">
                        Separate each highlight with a comma.
                      </small>

                      {highlightList.length > 0 && (
                        <div className="ep-chips mt-2">
                          {highlightList.map((h, i) => (
                            <span className="ep-chip" key={i}>
                              ✓ {h}
                            </span>
                          ))}
                        </div>
                      )}
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* ===== Right: images + save ===== */}
              <Col lg={5}>
                <Card className="border-0 shadow-sm ep-card ep-sticky">
                  <Card.Body className="p-4">
                    <div className="ep-legend mb-3">
                      <span className="ep-legend-icon">🖼</span>
                      <div>
                        <div className="ep-legend-title">Gallery</div>
                        <small className="ep-legend-hint">
                          {galleryImages.length} image
                          {galleryImages.length === 1 ? '' : 's'} · first is the
                          thumbnail
                        </small>
                      </div>
                    </div>

                    <label className="ep-drop">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(e) =>
                          setImageFiles(Array.from(e.target.files))
                        }
                      />
                      <span className="ep-drop-icon">⬆</span>
                      <span className="ep-drop-title">
                        Click to replace the gallery
                      </span>
                      <span
                        className={`ep-drop-hint${
                          imageFiles.length > 0 &&
                          imageFiles.length < MIN_IMAGES
                            ? ' is-warn'
                            : ''
                        }`}
                      >
                        {imageFiles.length === 0
                          ? `Leave empty to keep current · min ${MIN_IMAGES} to replace`
                          : imageFiles.length < MIN_IMAGES
                            ? `${imageFiles.length} selected — need ${
                                MIN_IMAGES - imageFiles.length
                              } more`
                            : `${imageFiles.length} selected ✓ replaces gallery`}
                      </span>
                    </label>

                    <div className="mt-3">
                      <ImageReorderGrid
                        images={galleryImages}
                        onMove={
                          imageFiles.length > 0
                            ? moveNewImage
                            : moveExistingImage
                        }
                        onRemove={
                          imageFiles.length > 0
                            ? removeNewImage
                            : removeExistingImage
                        }
                        onEdit={imageFiles.length > 0 ? openCropper : undefined}
                      />
                    </div>

                    <small className="ep-help d-block mt-2">
                      Use ◀ ▶ to reorder. The first image is the main thumbnail.
                    </small>

                    <div className="ep-actions mt-4">
                      <Button
                        type="submit"
                        className="ep-save"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="ep-spinner" /> Saving…
                          </>
                        ) : (
                          '💾 Save Changes'
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="light"
                        className="ep-cancel"
                        disabled={loading}
                        onClick={() => navigate(returnTo)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>

          <ImageCropperModal
            show={cropIndex !== null}
            src={cropSrc}
            onApply={applyCrop}
            onHide={() => {
              setCropIndex(null);
              setCropSrc(null);
            }}
          />
        </Container>
      </div>

      <style>{editProductStyles}</style>
    </>
  );
}

const editProductStyles = `
  .ep-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 42px) 0;
  }
  .min-w-0 { min-width: 0; }

  /* Hero */
  .ep-hero {
    position: relative;
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 55%, #ea580c 85%, #f59e0b);
    background-size: 220% 220%;
    animation: epDrift 16s ease-in-out infinite;
  }
  @keyframes epDrift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  .ep-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    pointer-events: none;
  }
  .ep-hero-blob-1 { width: 200px; height: 200px; background: #fbbf24; top: -90px; left: 20%; opacity: 0.45; }
  .ep-hero-blob-2 { width: 180px; height: 180px; background: #38bdf8; bottom: -90px; right: 8%; opacity: 0.3; }
  .ep-back {
    position: absolute;
    top: 16px;
    left: 18px;
    border: 1px solid rgba(255,255,255,0.3);
    background: rgba(255,255,255,0.14);
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    border-radius: 30px;
    padding: 5px 14px;
    backdrop-filter: blur(4px);
    transition: background 0.2s ease;
  }
  .ep-back:hover { background: rgba(255,255,255,0.26); }
  .ep-hero-thumb {
    width: 66px;
    height: 66px;
    object-fit: cover;
    border-radius: 16px;
    border: 2px solid rgba(255,255,255,0.5);
    flex-shrink: 0;
    background: #fff;
  }
  .ep-hero-icon {
    width: 58px;
    height: 58px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.7rem;
    border-radius: 16px;
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.3);
  }
  .ep-eyebrow {
    display: block;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    opacity: 0.85;
  }
  .ep-hero-title {
    font-size: clamp(1.4rem, 3.4vw, 2rem);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Cards */
  .ep-card { border-radius: 18px; }
  .ep-sticky { position: sticky; top: 90px; }

  .ep-legend {
    display: flex;
    align-items: center;
    gap: 11px;
    padding-bottom: 12px;
    border-bottom: 1px dashed #f1e4d0;
  }
  .ep-legend-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    border-radius: 13px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .ep-legend-title { font-weight: 800; font-size: 1.02rem; color: #7c2d12; line-height: 1.2; }
  .ep-legend-hint { color: #a8a29e; font-size: 0.75rem; }

  .ep-label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.74rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #78716c;
    margin-bottom: 6px;
  }
  .ep-req { color: #dc2626; }

  .ep-input {
    border-radius: 11px;
    border: 1px solid #ece7df;
    padding: 10px 13px;
  }
  .ep-input:focus {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.14);
  }
  .ep-input-warn { border-color: #fdba74; }
  .ep-money { border-radius: 11px; overflow: hidden; }
  .ep-money .input-group-text {
    background: #fff7ed;
    border: 1px solid #ece7df;
    border-right: none;
    color: #c2410c;
    font-weight: 800;
  }
  .ep-money .form-control {
    border: 1px solid #ece7df;
    border-left: none;
    padding: 10px 13px;
  }
  .ep-money .form-control:focus {
    border-color: #ea580c;
    box-shadow: none;
  }
  .ep-money:focus-within {
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.14);
    border-radius: 11px;
  }
  .ep-help { font-size: 0.72rem; color: #a8a29e; margin-top: 4px; }
  .ep-help-warn { color: #b45309; font-weight: 700; }

  /* Top-sale toggle */
  .ep-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 14px;
    border: 1px solid #ece7df;
    background: #fdfcfa;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  .ep-toggle.is-on {
    border-color: #fdba74;
    background: linear-gradient(135deg, #fffbeb, #fff7ed);
  }
  .ep-toggle-title { font-weight: 800; color: #1c1917; }
  .ep-toggle-hint { color: #a8a29e; font-size: 0.75rem; }
  .ep-toggle .form-check-input { cursor: pointer; }
  .ep-toggle .form-check-input:checked {
    background-color: #ea580c;
    border-color: #ea580c;
  }

  /* Highlight chips */
  .ep-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .ep-chip {
    font-size: 0.74rem;
    font-weight: 700;
    color: #15803d;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 20px;
    padding: 3px 11px;
  }

  /* Upload drop */
  .ep-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
    padding: 20px 16px;
    border-radius: 14px;
    border: 2px dashed #fdba74;
    background: #fffbeb;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  .ep-drop:hover {
    border-color: #ea580c;
    background: #fff;
    transform: translateY(-2px);
  }
  .ep-drop-icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    border-radius: 50%;
    background: #fff;
    border: 1px solid #fed7aa;
    color: #ea580c;
  }
  .ep-drop-title { font-weight: 800; color: #7c2d12; font-size: 0.9rem; }
  .ep-drop-hint { font-size: 0.74rem; color: #a16207; }
  .ep-drop-hint.is-warn { color: #b91c1c; font-weight: 700; }

  /* Actions */
  .ep-actions { display: flex; gap: 10px; }
  .ep-save {
    flex: 1 1 auto;
    border: none;
    border-radius: 12px;
    font-weight: 800;
    padding: 12px 18px;
    color: #fff;
    background: linear-gradient(120deg, #7c2d12, #ea580c 60%, #f59e0b);
    background-size: 180% auto;
    transition: background-position 0.4s ease, transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ep-save:hover:not(:disabled), .ep-save:focus:not(:disabled) {
    background: linear-gradient(120deg, #7c2d12, #ea580c 60%, #f59e0b);
    background-size: 180% auto;
    background-position: right center;
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(234,88,12,0.32);
  }
  .ep-save:disabled { opacity: 0.75; }
  .ep-cancel {
    border-radius: 12px;
    font-weight: 700;
    border: 1px solid #ece7df;
  }
  .ep-spinner {
    display: inline-block;
    width: 15px;
    height: 15px;
    margin-right: 7px;
    vertical-align: -2px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    animation: epSpin 0.8s linear infinite;
  }
  @keyframes epSpin { to { transform: rotate(360deg); } }

  @media (max-width: 991.98px) {
    .ep-sticky { position: static; }
  }
  @media (prefers-reduced-motion: reduce) {
    .ep-hero, .ep-drop, .ep-save, .ep-toggle { animation: none; transition: none; }
  }
`;

export default EditProductPage;
